import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { api, authPost, call } from "../lib/api.ts";
import { ErrorBox } from "../components/ui.tsx";
import { BrandLogo } from "../components/Shell.tsx";

const REASONS: Record<string, string> = {
  not_staff: "Esta conta não faz parte da equipe. Peça um convite a um administrador.",
  staff_inactive: "Seu acesso foi desativado. Fale com um administrador.",
  staff_domain: "Use o seu e-mail da empresa.",
  staff_sign_in_method: "Entre com a sua conta da empresa para acessar o admin.",
  google: "Não foi possível entrar com o Google. Tente de novo.",
};

export function SignInPage() {
  const { erro } = useSearch({ from: "/entrar" });
  const navigate = useNavigate();
  const config = useQuery({ queryKey: ["auth-config"], queryFn: () => call(api.admin["auth-config"].get()) });
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const origin = window.location.origin;

  async function google() {
    setBusy(true);
    setError(null);
    try {
      // Drop any non-staff session first so Google sign-in starts clean.
      await authPost("/sign-out").catch(() => {});
      const r = await authPost<{ url: string }>("/sign-in/social", {
        provider: "google",
        callbackURL: `${origin}/entrar/verificar`,
        errorCallbackURL: `${origin}/entrar?erro=google`,
      });
      window.location.href = r.url;
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  }

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authPost("/email-otp/send-verification-otp", { email, type: "sign-in" });
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authPost("/sign-in/email-otp", { email, otp });
      await navigate({ to: "/entrar/verificar" });
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  const domain = config.data?.domain;
  // Google only once it's configured; email codes when the API allows them (dev, or production
  // with STAFF_ALLOW_EMAIL_OTP=true).
  const showGoogle = config.data?.google === true;
  const showEmail = config.data?.emailOtp === true;
  return (
    <div className="auth">
      <div className="auth-card">
        <span className="brand" style={{ padding: 0 }}>
          <img className="brand-mark" src="/icon-96.png" alt="" />
          <BrandLogo />
          <span className="muted" style={{ fontSize: 12 }}>admin</span>
        </span>
        <div className="stack" style={{ gap: 6 }}>
          <h1>Entrar na operação</h1>
          <p>Acesso só para a equipe{domain ? <> com conta <b>@{domain}</b></> : null}. Depois do login, confirme com o código do app autenticador.</p>
        </div>
        {erro && <div className="error-box" role="alert">{REASONS[erro] ?? "Não foi possível entrar."}</div>}
        {showGoogle && (
          <button type="button" className="btn btn-primary btn-lg" onClick={google} disabled={busy}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.8z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z"/><path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8z"/><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z"/></svg>
            Entrar com Google
          </button>
        )}
        {config.data && !showGoogle && !showEmail && <p className="notice">Nenhuma forma de entrar configurada neste ambiente (GOOGLE_CLIENT_ID ou STAFF_ALLOW_EMAIL_OTP).</p>}
        {showEmail && (
          <>
            {showGoogle && <span className="divider">ou</span>}
            {!sent ? (
              <form className="stack" onSubmit={sendCode}>
                <label className="field">E-mail da empresa
                  <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`voce@${domain ?? "empresa.com.br"}`} autoComplete="email" />
                </label>
                <button type="submit" className={showGoogle ? "btn btn-lg" : "btn btn-primary btn-lg"} disabled={busy}>Receber código por e-mail</button>
              </form>
            ) : (
              <form className="stack" onSubmit={verifyCode}>
                <label className="field">Código enviado para {email}{config.data?.emailCodesLogged ? " (no log da API)" : ""}
                  <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
                </label>
                <button type="submit" className={showGoogle ? "btn btn-lg" : "btn btn-primary btn-lg"} disabled={busy || otp.length !== 6}>Entrar</button>
              </form>
            )}
          </>
        )}
        <ErrorBox error={error} />
      </div>
    </div>
  );
}
